import React, { useContext, useEffect, useState } from "react";
import { User, Tenants, FamilyInvitations } from "../../../service";
import ReusableForm from "../ReusableForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import toast from "react-hot-toast";
import AuthBgImage from "assets/images/auth_bg.jpg";
import { signUpFields } from "../../constant/Home/Authentication";
import { UserContext } from "../../context/UserContext";
import useApiHandler from "../../hooks/ApiHandler";
import Cookies from "universal-cookie";

const SignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Use global UserContext instead of AuthContext so the whole app sees the new user immediately
  const { setUserData } = useContext(UserContext);
  const { callApi } = useApiHandler();
  const [isTenantInvitation, setIsTenantInvitation] = useState(false);
  const [isFamilyInvitation, setIsFamilyInvitation] = useState(false);
  const [invitationToken, setInvitationToken] = useState(null); // tenant invite token
  const [familyInvitationToken, setFamilyInvitationToken] = useState(null); // family invite token (fi_token)

  // Check for tenant invitation token
  useEffect(() => {
    const token = searchParams.get("token");
    const fiToken = searchParams.get("fi_token");
    if (token) {
      setIsTenantInvitation(true);
      setInvitationToken(token);
    }
    if (fiToken) {
      setIsFamilyInvitation(true);
      setFamilyInvitationToken(fiToken);
    }

    // Removed automatic redirect to dashboard for logged-in users
    // This allows accessing the signup page even when logged in (useful for testing/creating multiple accounts)
    // const checkUserAuth = async () => {
    //   const cookies = new Cookies();
    //   const authToken = cookies.get("token");
    //
    //   // Only check authentication if there's a token and it's not a tenant invitation
    //   if (authToken && !token && !fiToken) {
    //     await callApi(() => User.getMe(), {
    //       onSuccess: (response) => {
    //         if (response.data) {
    //           setUserData(response.data);
    //           navigate("/dashboard");
    //         }
    //       },
    //       onError: () => {
    //         // User is not authenticated, stay on signup page
    //         console.log("User not authenticated, staying on signup page");
    //         // Clear invalid token
    //         cookies.remove("token", { path: "/" });
    //       },
    //     });
    //   }
    // };
    //
    // checkUserAuth();
  }, [searchParams]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const password = values?.password ?? "";
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      setSubmitting(false);
      return;
    }

    if (isFamilyInvitation) {
      // Handle family invitation acceptance (fi_token)
      const payload = {
        fi_token: familyInvitationToken,
        password: values.password,
        family_member_name: values.full_name || undefined,
      };

      await callApi(() => FamilyInvitations.accept(payload), {
        successMessage: "Account created successfully!",
        onSuccess: async (response) => {
          window.fbq?.('track', 'CompleteRegistration');
          // Set the token from response if available and refresh user context
          const token = response?.data?.access || response?.data?.token;
          if (token) {
            const cookies = new Cookies();
            cookies.set("token", token, {
              path: "/",
              sameSite: "lax",
              secure: window.location.protocol === "https:",
            });
          }
          try {
            const me = await User.getMe();
            if (me?.data) setUserData(me.data);
          } catch (_) {}
          navigate("/dashboard");
        },
        onError: (err) => {
          console.error("Family invitation acceptance failed:", err);
        },
        finallyCallback: () => {
          setSubmitting(false);
        },
      });
    } else if (isTenantInvitation) {
      // Handle tenant invitation acceptance
      const payload = {
        token: invitationToken,
        password: values.password,
        tenant_name: values.full_name || undefined,
      };

      await callApi(() => Tenants.acceptTenantInvitation(payload), {
        successMessage: "Account created successfully!",
        onSuccess: async (response) => {
          window.fbq?.('track', 'CompleteRegistration');
          // Set the token from response if available and refresh user context
          const token = response?.data?.access;
          if (token) {
            const cookies = new Cookies();
            cookies.set("token", token, {
              path: "/",
              sameSite: "lax",
              secure: window.location.protocol === "https:",
            });
          }
          try {
            const me = await User.getMe();
            if (me?.data) setUserData(me.data);
          } catch (_) {}
          navigate("/dashboard");
        },
        onError: (err) => {
          console.error("Tenant invitation acceptance failed:", err);
        },
        finallyCallback: () => {
          setSubmitting(false);
        },
      });
    } else {
      // Handle regular signup
      await callApi(() => User.register(values), {
        successMessage: "Logged in successfully!",
        onSuccess: async (response) => {
          window.fbq?.('track', 'CompleteRegistration');
          // Persist user into global context so subsequent flows (e.g., vendor onboarding) can prefill
          const user = response?.data?.user || {};
          if (user && Object.keys(user).length > 0) {
            setUserData(user);
          }

          // If API returns access token on register, store it
          const accessToken = response?.data?.access || response?.data?.token;
          if (accessToken) {
            const cookies = new Cookies();
            cookies.set("token", accessToken, {
              path: "/",
              sameSite: "lax",
              secure: window.location.protocol === "https:",
            });
          }

          // Route based on account type
          const accountType = user?.account_type || user?.accountType;
          if (
            ["LANDLORD", "PROPERTY_MANAGER", "HOMEOWNER", "STR_HOST"].includes(accountType)
          ) {
            //navigate("/post-signup-thank-you");
            navigate("/dashboard")
          } else if (accountType === "VENDOR") {
            navigate("/vendor-onboarding");
          } else {
            //navigate("/post-signup-thank-you");
            navigate("/dashboard")
          }
        },
        onError: (err) => {
          console.error("Registration failed:", err);
        },
        finallyCallback: () => {
          setSubmitting(false);
        },
      });
    }
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center relative p-4 md:p-6 lg:p-8 "
      style={{ backgroundImage: `url(${AuthBgImage})` }}
    >
      <Toaster />
      <div
        className="absolute inset-0 bg-black"
        style={{ backgroundColor: "#00000033" }}
      />

      <div className="flex flex-col rounded-[20px] p-4 md:p-6 lg:p-8  max-w-[600px]  justify-center items-center bg-white z-10">
        <ReusableForm
          title={
            isTenantInvitation || isFamilyInvitation
              ? "Complete Your Account Setup"
              : "Welcome to Properteasy"
          }
          description={
            isTenantInvitation || isFamilyInvitation
              ? "You've been invited to join a property. Complete your account setup to get started."
              : "Sign up to simplify your maintenance needs."
          }
          initialValues={{
            full_name: "",
            email: "",
            password: "",
            account_type: searchParams.get("account_type") || "",
          }}
          fields={
            isTenantInvitation || isFamilyInvitation
              ? signUpFields.filter(
                  (field) =>
                    field.name !== "email" && field.name !== "account_type"
                )
              : signUpFields
          }
          handleClick={handleSubmit}
          submitButtonText={
            isTenantInvitation || isFamilyInvitation ? "Complete Setup" : "Create Account"
          }
          showTerms={true}
        >
          <p className="text-center  text-[14px] text-[#767676] tracking-[0.025em] font-medium leading-6">
            Already have an account?{" "}
            <span
              className="text-[#2C8D48] cursor-pointer"
              onClick={() => navigate("/login")}
            >
              {" "}
              Log In
            </span>
          </p>
        </ReusableForm>
      </div>
    </div>
  );
};

export default SignUp;
